import { useMemo } from "react";

import { type TSectionInitialData } from "@/api/dashboard/dashboard.types";
import { FilterPillsBar } from "@/components/dashboard/FilterPillsBar";
import { AppCard } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { useCategorySelection } from "@/hooks/useCategorySelection";
import { ToolsGrid, ToolsGridSkeleton } from "./ToolsFeature";

const MY_DEAL_STACK_DESCRIPTION =
  "Manage and track your deal pipeline across sourcing, diligence, and execution. Organize opportunities, monitor deal stages, and collaborate with your team to drive informed investment decisions.";

/** Dummy-data tool URLs (host "mydealstack") must not render as real links. */
function isPlaceholderUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === "mydealstack" || hostname.endsWith(".mydealstack");
  } catch {
    return true;
  }
}

type TMyDealStackFeatureProps = {
  companyId?: string | null;
  sectionId?: string | null;
  dashboardData?: TSectionInitialData | null;
  isLoading?: boolean;
  error?: string | null;
};

/**
 * My Deal Stack dashboard body. Renders two siblings inside PageLayout's flex
 * column: a fixed category filter bar, then the scrollable area holding the
 * section title/description card and the tools grid.
 */
export function MyDealStackFeature({
  companyId: _companyId,
  sectionId: _sectionId,
  dashboardData = null,
  isLoading = false,
  error = null,
}: TMyDealStackFeatureProps) {
  const categories = dashboardData?.categories ?? [];
  const tools = useMemo(
    () => (dashboardData?.tools ?? []).filter((tool) => !isPlaceholderUrl(tool.url)),
    [dashboardData?.tools]
  );
  const { selectedCategoryIds, isAllSelected, handleAllSelect, handleCategoryToggle } =
    useCategorySelection(categories);

  const filteredTools = useMemo(() => {
    if (isAllSelected || selectedCategoryIds.length === 0) {
      return tools;
    }
    return tools.filter((tool) => tool.categoryIds.some((id) => selectedCategoryIds.includes(id)));
  }, [isAllSelected, selectedCategoryIds, tools]);

  return (
    <>
      {/* Fixed category filter bar — stays put while the content below scrolls */}
      {!error && categories.length > 0 && (
        <div className="flex-shrink-0">
          <FilterPillsBar
            footer={
              <p className="mt-2 text-xs text-muted-foreground sm:text-sm">
                The categories are steps of the fundraising process — click a step to see the
                tools that can help with it.
              </p>
            }
          >
            <Button
              size="sm"
              variant={isAllSelected ? "default" : "outline"}
              onClick={(e) => { handleAllSelect(); e.currentTarget.blur(); }}
              disabled={isLoading}
              className="flex-shrink-0"
            >
              All
            </Button>
            {categories.map((category, index) => (
              <Button
                key={category.categoryId}
                size="sm"
                variant={selectedCategoryIds.includes(category.categoryId) ? "default" : "outline"}
                onClick={(e) => { handleCategoryToggle(category.categoryId); e.currentTarget.blur(); }}
                disabled={isLoading}
                className="flex-shrink-0"
              >
                {index + 1}. {category.categoryName}
              </Button>
            ))}
          </FilterPillsBar>
        </div>
      )}

      {/* Scrollable content */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex w-full flex-col gap-6">
          {/* Title and description block */}
          <AppCard className="p-4 sm:p-6">
            <h2 className="text-base font-semibold leading-tight text-foreground sm:text-lg">My Deal Stack</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:mt-4">{MY_DEAL_STACK_DESCRIPTION}</p>
          </AppCard>

          {error ? (
            <p className="text-xs text-destructive sm:text-sm">{error}</p>
          ) : isLoading ? (
            <ToolsGridSkeleton />
          ) : (
            <ToolsGrid
              tools={filteredTools}
              emptyMessage={
                categories.length > 0
                  ? "No tools match the selected categories yet."
                  : "No tools available for this section yet."
              }
            />
          )}
        </div>
      </div>
    </>
  );
}
